/* eslint-disable jsx-a11y/alt-text */
import { Button, Container, Stack } from '@mui/material'
import { FC, useCallback, useEffect, useRef, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { Footer } from '../../components/Footer'
import { Form, FormProps } from '../../components/Form'
import {
  Goods,
  isPack,
  isPlan,
  mapPacks,
  mapPlans,
  mapProducts,
} from '../../components/Goods'
import { Header } from '../../components/Header'
import { Modal } from '../../components/Modal'
import {
  Promo,
  PromoCodeField,
  PromoCodeFieldProps,
} from '../../components/PromoCodeField'
import { useGet } from '../../hooks/get'
import { usePurchase } from '../../hooks/purchase'
import { useSign } from '../../hooks/sign'
import { useUser } from '../../hooks/user'

const Purchase: FC = () => {
  const params = useParams()
  const { t } = useTranslation()

  const { user } = useUser()
  const sign = useSign()

  const { data: item } = useGet<Dto.Plan | Dto.Pack | Dto.Product>(
    user && `member_portal/${params.type}s/${params.id}`
  )

  const purchase = usePurchase(item)

  const [result, setResult] = useState<string>()
  const promoRef = useRef<Promo>()

  const handlePromoCodeApply = useCallback<PromoCodeFieldProps['onApply']>(
    (promo) => {
      promoRef.current = promo
    },
    []
  )

  const handleSubmit: FormProps['onSubmit'] = async (fields) => {
    if (!loaded) {
      return
    }

    try {
      await purchase.submit({ promo: promoRef.current })
      setResult(t('Purchase succeeded. You can close this page safely.'))
    } catch (error) {
      if (error instanceof Error) {
        toast.error(error.message)
      }
    }
  }

  const loaded = !!(user && item)

  useEffect(() => {
    if (!user) {
      sign.start()
    }
  }, [sign, user])

  useEffect(() => {
    if (!isPlan(item) || item.latest_version_id === item.id) {
      return
    }

    window.location.replace(`./${item.latest_version_id}`)
  }, [item])

  return (
    <>
      <Helmet>
        <title>{t('Purchase a {{type}}', { type: params.type })}</title>
      </Helmet>
      <Header />
      {loaded && (
        <Container>
          <Stack alignItems="center" spacing={5} py={5}>
            <Goods
              items={
                isPlan(item)
                  ? mapPlans([item])
                  : isPack(item)
                  ? mapPacks([item])
                  : mapProducts([item])
              }
              defaultSelected={item.id}
              large
            />
            <Stack alignItems="inherit" spacing={2}>
              <PromoCodeField goods={item} onApply={handlePromoCodeApply} />
              <Form onSubmit={handleSubmit}>
                <Stack alignItems="center" spacing={2}>
                  {purchase.tcElement}
                  <Button
                    type="submit"
                    loading={purchase.pending}
                    sx={{ minInlineSize: '8em' }}
                  >
                    {t('Continue')}
                  </Button>
                </Stack>
              </Form>
            </Stack>
            <Footer />
          </Stack>
        </Container>
      )}
      {sign.element}
      <Modal open={!!result}>{result}</Modal>
      {purchase.paymentMethodElement}
    </>
  )
}

export { Purchase }
